import React, { useState, useEffect } from 'react';
import { Heart, User as UserIcon, MapPin, ShoppingBag, Share2, Coins, Lock } from 'lucide-react';
import { useAuth } from '../Auth/AuthContext';
import { ProfileService } from './service/Profile.service';
import { User } from './dto/Profile.dto';
import { ApiService } from '../../shared/service/Apiservice';
import axios from 'axios';
import './ProfilSideBar.css';

type MenuKey = 'profile' | 'orders' | 'address' | 'referrals' | 'build-coins' | 'wishlist' | 'privacy';
type ActiveViewType = 'profile' | 'orders' | 'order-details' | 'address' | 'referrals' | 'build-coins' | 'wishlist' | 'privacy';

interface ProfileSideBarProps {
  activeView: ActiveViewType;
  onViewChange: (view: ActiveViewType) => void;
}

const ProfileSideBar: React.FC<ProfileSideBarProps> = ({ activeView, onViewChange }) => {
  const { logout, isAuthenticated } = useAuth();
  const [user, setUser] = useState<User | null>(null);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  // Create API service instances
  const apiService = React.useMemo(() => new ApiService(axios.create()), []);
  const profileService = React.useMemo(() => new ProfileService(apiService), [apiService]);

  // Fetch user data
  const fetchUserData = async () => {
    try {
      const profileResponse = await profileService.getUserProfile();
      if (profileResponse.success && profileResponse.user) {
        setUser(profileResponse.user);
      }
    } catch (err) {
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchUserData();
    }
  }, [isAuthenticated]);

  const handleLogout = async () => {
    try {
      setIsLoggingOut(true);
      await logout();
    } catch (error) {
    } finally {
      setIsLoggingOut(false);
    }
  };
  const onSelect = (key: MenuKey) => {
    if (['profile', 'orders', 'address', 'referrals', 'build-coins', 'wishlist', 'privacy'].includes(key)) {
      onViewChange(key as ActiveViewType);
    }
  };

  const renderMenuItem = (key: MenuKey, iconPath: string | React.ReactNode, label: string) => {
    const isActive = activeView === key;
    return (
      <button
        key={key}
        className={`profile-menu-item ${isActive ? 'active' : ''}`}
        onClick={() => onSelect(key)}
        type="button"
      >
        <div className="menu-icon-container">
          {typeof iconPath === 'string' ? (
            <img 
              src={iconPath} 
              alt={key} 
              className={`profile-menu-icon ${key.toLowerCase()}-icon`} 
            />
          ) : (
            <div className={`profile-menu-svg-icon ${key.toLowerCase()}-svg-icon ${isActive ? 'active' : ''}`}>
              {iconPath}
            </div>
          )}
        </div>
        <span className="profile-menu-text">{label}</span>
      </button>
    );
  };

  return (
    <div className="profile-page">
      <div className="profile-card">
        <div className="profile-avatar" style={{ display: 'none' }}>
          <img src="/icon/profile.png" alt="profile" />
        </div>
        <div className="profile-user">
          <h3 className="profile-name" style={{ display: 'none' }}>{user?.name || 'User'}</h3>
          <p className="profile-email" style={{ display: 'none' }}>{user?.email || 'Loading...'}</p>
          <p className="profile-role" style={{ display: 'none' }}>{user?.role || 'User'}</p>
        </div>

        <div className="build-coins">
          <div className="build-coins-content">
            <span className="build-coins-title">Build Coins</span>
            <span className="build-coins-value">
              Available: {Math.round((user?.referralCredits || 0) * 100) / 100} coins
            </span>
          </div>
        </div>

        <div className="profile-menu">
          {renderMenuItem('profile', <UserIcon size={20} strokeWidth={2} />, 'Profile')}
          <div className="menu-divider" />
          {renderMenuItem('address', <MapPin size={20} strokeWidth={2} />, 'Address')}
          <div className="menu-divider" />
          {renderMenuItem('orders', <ShoppingBag size={20} strokeWidth={2} />, 'Orders')}
          <div className="menu-divider" />
          {renderMenuItem('wishlist', <Heart size={20} strokeWidth={2} />, 'Wishlist')}
          <div className="menu-divider" />
          {renderMenuItem('referrals', <Share2 size={20} strokeWidth={2} />, 'Referrals')}
          <div className="menu-divider" />
          {renderMenuItem('build-coins', <Coins size={20} strokeWidth={2} />, 'Build Coins')}
          <div className="menu-divider" />
          {renderMenuItem('privacy', <Lock size={20} strokeWidth={2} />, 'Privacy')}
        </div>

        <button className="logout-btn" type="button" onClick={handleLogout} disabled={isLoggingOut}>
          <img src="/icon/logout.png" alt="logout" className="logout-icon" />
          <span className="logout-text">{isLoggingOut ? 'Logging out...' : 'Logout'}</span>
        </button>
      </div>
    </div>
  );
};

export default ProfileSideBar;
